import { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Section/route title shown in the fallback, e.g. "Klienti". */
  title?: string;
  /** Called when the user retries — use to reset local state/refetch. */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

/**
 * F18: route/section-level error boundary for admin pages.
 *
 * Wraps a single admin route's element so a render crash inside that
 * page shows a scoped fallback instead of tripping the app-wide
 * `ErrorBoundary` (which blanks the whole admin shell). "Skúsiť znova"
 * resets local boundary state without a full page reload.
 */
class AdminSectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AdminSectionErrorBoundary] Uncaught error:", error, info);
  }

  handleRetry = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    const { title } = this.props;
    return (
      <div className="min-h-[50vh] flex items-start justify-center p-4 sm:p-8">
        <div className="max-w-xl w-full mt-8 space-y-4 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <h1 className="text-lg font-bold text-destructive">
            {title ? `Nastala chyba v sekcii „${title}“` : "Nastala chyba pri načítavaní tejto sekcie"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Skúste to znova. Ak chyba pretrváva, obnovte stránku alebo kontaktujte vývojára.
          </p>
          <pre className="text-xs bg-background border border-border rounded-md p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words">
            {error.message}
          </pre>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90"
            >
              <RefreshCw className="w-4 h-4" /> Skúsiť znova
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
            >
              Obnoviť stránku
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AdminSectionErrorBoundary;
